import { Warning } from "@mui/icons-material";
import { Alert, Avatar, Button, Dialog, DialogActions, DialogContent, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { alpha, Box, Grid } from "@mui/system";
import { ApartmentDto, RemoveTenantFromApartment } from "../../../../types/propertyInventory/Apartment";
import { PaymentStatus } from "../../../../types/propertyInventory/RentPayment";
import { usePropertyInventory } from "../../../../hooks/usePropertyInventory";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";

interface DeleteCurrentTenantProps {
    removeTenantDialogOpen: boolean;
    setRemoveTenantDialogOpen: (open: boolean) => void;
    apartmentById: ApartmentDto | null;
    handleRemoveTenant: () => void;
}
export const DeleteCurrentTenant = (props: DeleteCurrentTenantProps) => {
    const { removeTenantDialogOpen, setRemoveTenantDialogOpen, apartmentById, handleRemoveTenant } = props;
    const [removeTenantData, setRemoveTenantData] = useState<RemoveTenantFromApartment>({
        moveOutDate: new Date().toISOString(),
        reasonForLeaving: '',
        securityDepositReturned: 0,
        notes: '',
        apartmentId: apartmentById?.id || '',
        tenantId: apartmentById?.currentTenant?.id || ''
    });
    const { removeTenantFromApartment, isCreating, errorMessage } = usePropertyInventory();
    const [isError, setIsError] = useState(false);
    const [errorText, setErrorText] = useState('');

    useEffect(() => {
        if (apartmentById) {
            setRemoveTenantData((prevData) => ({
                ...prevData,
                apartmentId: apartmentById.id,
                tenantId: apartmentById.currentTenant?.id || ''
            }));
        }
    }, [apartmentById]);

    const handleErrorText = () => {
        let message = '';
        const today = new Date();
        if(removeTenantData.reasonForLeaving === '') {
            message += 'El motivo de salida es obligatorio/n';
        } 
        if(!removeTenantData.moveOutDate) {
            message += 'La fecha de salida es obligatoria/n';
        }
        if(removeTenantData.securityDepositReturned < 0) {
            message += 'El depósito de seguridad devuelto no puede ser negativo/n';
        }
        if(removeTenantData.moveOutDate < today.toISOString().split('T')[0]) {
            message += 'La fecha de salida no puede ser anterior a hoy/n';
        }
        if(message) {
            setIsError(true);
            setErrorText(message);
            return;
        }
    }

    const handleConfirmRemoveTenant = async () => {
        setIsError(false);
        handleErrorText();
        if (isError && errorText) return;
        
        await removeTenantFromApartment(removeTenantData);
        if(!errorMessage) {
            setRemoveTenantDialogOpen(false);
            handleRemoveTenant();
            Swal.fire({
                icon: 'success',
                title: 'Inquilino Retirado',
                text: 'El inquilino ha sido retirado exitosamente.',
                confirmButtonColor: '#4DB6AC',
            });
        }else{
            setIsError(true);
            setErrorText(errorMessage || 'Error al retirar el inquilino');
        }
    }

    const isValid = removeTenantData.moveOutDate && removeTenantData.reasonForLeaving;

    return (
        <Dialog
            open={removeTenantDialogOpen}
            onClose={() => setRemoveTenantDialogOpen(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogContent sx={{ p: 4 }}>
                {/* Icono de advertencia */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: alpha('#FF9800', 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: 2
                        }}
                    >
                        <Warning sx={{ fontSize: 48, color: '#FF9800' }} />
                    </Box>

                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        ¿Retirar Inquilino?
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Se registrará la salida del inquilino actual y el apartamento quedará disponible
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Información del inquilino */}
                <Box
                    sx={{
                        backgroundColor: alpha('#4DB6AC', 0.05),
                        borderRadius: 2,
                        p: 2.5,
                        mb: 3
                    }}
                >
                    <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ mb: 1.5, display: 'block' }}>
                        Inquilino Actual
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                            sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: '#4DB6AC',
                                fontSize: '1.2rem'
                            }}
                        >
                            {apartmentById?.currentTenant?.fullName.charAt(0)}
                        </Avatar>
                        <Box>
                            <Typography variant="body1" fontWeight="bold">
                                {apartmentById?.currentTenant?.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {apartmentById?.currentTenant?.primaryPhone}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Apartamento:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                            {apartmentById?.apartmentNumber}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                            Fecha de Ingreso:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                            {apartmentById?.currentTenant?.createdAt
                                ? new Date(apartmentById.currentTenant.createdAt).toLocaleDateString('es-CR')
                                : 'No especificado'}
                        </Typography>
                    </Box>
                </Box>

                {/* Formulario de datos de salida */}
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Fecha de Salida"
                            onChange={(e) => setRemoveTenantData((prevData) => ({
                                ...prevData,
                                moveOutDate: e.target.value
                            }))}
                            value={removeTenantData.moveOutDate}
                            type="date"
                            defaultValue={new Date().toISOString().split('T')[0]}
                            InputLabelProps={{ shrink: true }}
                            required
                            helperText="Fecha en la que el inquilino dejó el apartamento"
                        />
                    </Grid>

                    <Grid size={12}>
                        <FormControl fullWidth>
                            <InputLabel>Motivo de Salida</InputLabel>
                            <Select label="Motivo de Salida" value={removeTenantData.reasonForLeaving} onChange={(e) => setRemoveTenantData((prevData) => ({
                                ...prevData,
                                reasonForLeaving: e.target.value
                            }))}>
                                <MenuItem value="Finalización de contrato">
                                    📋 Finalización de contrato
                                </MenuItem>
                                <MenuItem value="Mudanza voluntaria">
                                    🏠 Mudanza voluntaria
                                </MenuItem>
                                <MenuItem value="Incumplimiento de contrato">
                                    ⚠️ Incumplimiento de contrato
                                </MenuItem>
                                <MenuItem value="Problemas de pago">
                                    💰 Problemas de pago
                                </MenuItem>
                                <MenuItem value="Renovación del apartamento">
                                    🔧 Renovación del apartamento
                                </MenuItem>
                                <MenuItem value="Otro">
                                    📝 Otro
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Depósito de Seguridad Devuelto"
                            type="number"
                            value={removeTenantData.securityDepositReturned}
                            onChange={(e) => setRemoveTenantData((prevData) => ({
                                ...prevData,
                                securityDepositReturned: Number(e.target.value)
                            }))}
                            placeholder="0.00"
                            required
                            inputProps={{ min: 0 }}
                            helperText="Monto del depósito de seguridad que se devuelve al inquilino"
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Notas Adicionales"
                            multiline
                            value={removeTenantData.notes}
                            onChange={(e) => setRemoveTenantData((prevData) => ({
                                ...prevData,
                                notes: e.target.value
                            }))}
                            rows={3}
                            placeholder="Información adicional sobre la salida del inquilino, estado del apartamento, etc."
                            helperText="Detalles relevantes sobre el proceso de salida (opcional)"
                        />
                    </Grid>

                    {/* Advertencia sobre pagos pendientes */}
                    {apartmentById?.currentTenant?.rentPayments?.some((p: any) => p.status === PaymentStatus.pendiente || p.status === PaymentStatus.atrasado) && (
                        <Grid size={12}>
                            <Box
                                sx={{
                                    backgroundColor: alpha('#FF9800', 0.1),
                                    border: `1px solid ${alpha('#FF9800', 0.3)}`,
                                    borderRadius: 2,
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5
                                }}
                            >
                                <Warning sx={{ color: '#FF9800', fontSize: 20, mt: 0.2 }} />
                                <Box>
                                    <Typography variant="body2" fontWeight="medium" color="#FF9800" gutterBottom>
                                        Atención: Pagos Pendientes
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Este inquilino tiene pagos pendientes o atrasados. Asegúrate de resolver esto antes de completar el retiro.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Información adicional */}
                <Box
                    sx={{
                        backgroundColor: alpha('#2196F3', 0.05),
                        borderRadius: 1.5,
                        p: 2
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        💡 <strong>Información importante:</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        • El inquilino se moverá al historial del apartamento
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        • El apartamento quedará con estado "Disponible"
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • Podrás asignar un nuevo inquilino posteriormente
                    </Typography>
                </Box>
                {isError && (
                   <Alert severity="error" sx={{ mt: 3, whiteSpace: 'pre-line' }}>{errorText}</Alert>
                )}
            </DialogContent>


            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Button
                    onClick={() => setRemoveTenantDialogOpen(false)}
                    variant="outlined"
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirmRemoveTenant}
                    variant="contained"
                    disabled={!isValid || isCreating}
                    sx={{
                        backgroundColor: '#FF9800',
                        '&:hover': {
                            backgroundColor: '#F57C00'
                        }
                    }}
                >
                    Confirmar Retiro
                </Button>
            </DialogActions>
        </Dialog>
    )
};